import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import {
  DashboardPage,
  QueuePage,
  SettingsPage,
  ProfilePage,
  LoginPage,
  SignUpPage,
  ForgotPasswordPage,
  ResetPasswordPage,
} from './pages';
import { useAppContext } from './context/AppContext';
import Navbar from './components/Navbar';
import StatusToast from './components/StatusToast';
import './assets/globals.css';

function ProtectedRoute() {
  const { token, authReady } = useAppContext();

  if (!authReady) {
    return (
      <div className="step-loading">
        <p className="step-loading-text">Restoring your session…</p>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

function DashboardLayout({ darkMode, onToggleDark }) {
  return (
    <div className="app-layout">
      <Navbar darkMode={darkMode} onToggleDark={onToggleDark} />
      <main className="app-main">
        <Outlet />
      </main>
    </div>
  );
}

function App() {
  const { token, setToken } = useAppContext();
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem('theme') === 'dark',
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    // Password-reset emails use ?token= on /reset-password — do not treat that as a session token.
    if (window.location.pathname.startsWith('/reset-password')) {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    if (urlToken && urlToken.length > 25) {
      setToken(urlToken);
      localStorage.setItem('slipstream_token', urlToken);
    }
  }, [setToken]);

  return (
    <Router>
      <StatusToast />
      <Routes>
        <Route path="/login" element={<LoginPage darkMode={darkMode} />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage darkMode={darkMode} />} />
        <Route path="/reset-password" element={<ResetPasswordPage darkMode={darkMode} />} />
        <Route path="/sign-up/:plan?" element={<SignUpPage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<DashboardLayout darkMode={darkMode} onToggleDark={() => setDarkMode((d) => !d)} />}>
            <Route index element={<DashboardPage />} />
            <Route path="queue" element={<QueuePage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to={token ? '/' : '/login'} replace />} />
      </Routes>
    </Router>
  );
}

export default App;
